'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import Link from 'next/link';

const DEFAULT_FPS = 30;

function formatTimestamp(seconds: number): string {
  const totalMs = Math.round(seconds * 1000);
  const ms = totalMs % 1000;
  const totalSec = Math.floor(totalMs / 1000);
  const sec = totalSec % 60;
  const min = Math.floor(totalSec / 60);
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

interface TriggerEntry {
  id: string;
  time: number;
  label: string;
}

// ── Seek Bar with Trigger Markers ─────────────────────────────────────────────

interface SeekBarProps {
  currentTime: number;
  duration: number;
  triggers: TriggerEntry[];
  fps: number;
  onSeek: (time: number) => void;
}

function SeekBar({ currentTime, duration, triggers, fps, onSeek }: SeekBarProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragTime, setDragTime] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const displayTime = isDragging && dragTime !== null ? dragTime : currentTime;
  const progress = duration > 0 ? (displayTime / duration) * 100 : 0;

  const getTimeFromEvent = useCallback((e: React.MouseEvent | MouseEvent): number => {
    const track = trackRef.current;
    if (!track || duration <= 0) return 0;
    const rect = track.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    return (x / rect.width) * duration;
  }, [duration]);

  const handleTrackMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    const t = getTimeFromEvent(e);
    setDragTime(t);
    onSeek(t);
  }, [getTimeFromEvent, onSeek]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const t = getTimeFromEvent(e);
      setDragTime(t);
      onSeek(t);
    };
    const onUp = () => {
      setIsDragging(false);
      setDragTime(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, getTimeFromEvent, onSeek]);

  if (duration <= 0) return null;

  return (
    <div className="w-full px-1 py-2 select-none">
      {/* Track */}
      <div
        ref={trackRef}
        className="relative w-full rounded-full cursor-pointer"
        style={{ height: 6, background: 'rgba(255,255,255,0.1)' }}
        onMouseDown={handleTrackMouseDown}
      >
        {/* Filled portion */}
        <div
          className="absolute left-0 top-0 h-full rounded-full pointer-events-none"
          style={{
            width: `${progress}%`,
            background: '#00e5ff',
            transition: isDragging ? 'none' : 'width 0.05s linear',
          }}
        />
        {/* Trigger markers */}
        {triggers.map((trigger) => {
          const pct = (trigger.time / duration) * 100;
          return (
            <div
              key={trigger.id}
              className="absolute top-1/2 pointer-events-none"
              style={{
                left: `${pct}%`,
                transform: 'translate(-50%, -50%)',
                width: 3,
                height: 12,
                background: '#ff4655',
                borderRadius: 2,
                boxShadow: '0 0 4px #ff465588',
                opacity: 0.9,
                zIndex: 1,
              }}
              title={`${trigger.label} — ${trigger.time.toFixed(3)}s`}
            />
          );
        })}
        {/* Scrub handle */}
        <div
          className="absolute top-1/2 pointer-events-none"
          style={{
            left: `${progress}%`,
            transform: 'translate(-50%, -50%)',
            width: isDragging ? 16 : 12,
            height: isDragging ? 16 : 12,
            background: '#fff',
            borderRadius: '50%',
            boxShadow: '0 0 0 2px #00e5ff, 0 2px 8px rgba(0,0,0,0.5)',
            transition: isDragging ? 'none' : 'width 0.1s, height 0.1s',
            zIndex: 2,
          }}
        />
      </div>
      {/* Time labels */}
      <div className="flex justify-between mt-1.5">
        <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>
          {formatTimestamp(displayTime)}
        </span>
        {triggers.length > 0 && (
          <span className="text-xs font-mono" style={{ color: 'rgba(255,70,85,0.6)', fontSize: '10px' }}>
            {triggers.length} trigger{triggers.length !== 1 ? 's' : ''} marked
          </span>
        )}
        <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.25)', fontSize: '10px' }}>
          {formatTimestamp(duration)}
        </span>
      </div>
    </div>
  );
}

export default function TimestampToolPage() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [videoFileName, setVideoFileName] = useState<string>('');
  const [videoUrl, setVideoUrl] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [fps, setFps] = useState(DEFAULT_FPS);
  const [copied, setCopied] = useState(false);
  const [fpsInput, setFpsInput] = useState(String(DEFAULT_FPS));
  const [triggers, setTriggers] = useState<TriggerEntry[]>([]);
  const [copiedTriggerId, setCopiedTriggerId] = useState<string | null>(null);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [editingLabelValue, setEditingLabelValue] = useState('');

  // rAF loop to update currentTime display
  const startRaf = useCallback(() => {
    const tick = () => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const stopRaf = useCallback(() => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  useEffect(() => {
    startRaf();
    return stopRaf;
  }, [startRaf, stopRaf]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setVideoSrc(url);
    setVideoFileName(file.name);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setTriggers([]);
  };

  const handleUrlLoad = () => {
    if (!videoUrl.trim()) return;
    setVideoSrc(videoUrl.trim());
    setVideoFileName(videoUrl.trim().split('/').pop() || 'Remote video');
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setTriggers([]);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const stepFrame = (direction: 1 | -1) => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    setIsPlaying(false);
    const frameDuration = 1 / fps;
    videoRef.current.currentTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + direction * frameDuration));
  };

  const copyTimestamp = async () => {
    const val = currentTime.toFixed(6);
    try {
      await navigator.clipboard.writeText(val);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback */ }
  };

  const handleFpsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFpsInput(e.target.value);
    const parsed = parseFloat(e.target.value);
    if (!isNaN(parsed) && parsed > 0) setFps(Math.min(parsed, 120));
  };

  const handleSeek = useCallback((t: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = t;
    setCurrentTime(t);
  }, []);

  const addTrigger = () => {
    const id = `t-${Date.now()}`;
    const newTrigger: TriggerEntry = {
      id,
      time: currentTime,
      label: `Trigger ${triggers.length + 1}`,
    };
    setTriggers((prev) => [...prev, newTrigger].sort((a, b) => a.time - b.time));
  };

  const removeTrigger = (id: string) => {
    setTriggers((prev) => prev.filter((t) => t.id !== id));
  };

  const jumpToTrigger = (time: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const copyTriggerTime = async (id: string, time: number) => {
    try {
      await navigator.clipboard.writeText(time.toFixed(6));
      setCopiedTriggerId(id);
      setTimeout(() => setCopiedTriggerId(null), 2000);
    } catch { /* fallback */ }
  };

  const startEditLabel = (id: string, label: string) => {
    setEditingLabelId(id);
    setEditingLabelValue(label);
  };

  const commitLabel = () => {
    if (!editingLabelId) return;
    setTriggers((prev) => prev.map((t) => t.id === editingLabelId ? { ...t, label: editingLabelValue || t.label } : t));
    setEditingLabelId(null);
  };

  const exportConfig = () => {
    if (triggers.length === 0) return;
    const lines = [
      `// Trigger timestamps for: ${videoFileName || 'your video'}`,
      `// Generated by fps.expert Timestamp Scrubber`,
      ``,
      `const clipConfig = {`,
      `  id: 'my-clip',`,
      `  label: '${videoFileName || 'My Clip'}',`,
      `  src: '${videoSrc || '/videos/your-clip.mp4'}',`,
      `  triggers: [`,
      ...triggers.map((t) => `    { triggerTime: ${t.time.toFixed(6)}, label: '${t.label}' },`),
      `  ],`,
      `};`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clip-config.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen flex flex-col overflow-y-auto" style={{ background: '#0a0a0a', fontFamily: 'var(--font-geist), sans-serif', color: '#fff' }}>
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b sticky top-0 z-10"
        style={{ borderColor: 'rgba(255,255,255,0.07)', background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(16px)' }}>
        {/* BACK button */}
        <Link href="/"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono font-bold tracking-widest uppercase transition-colors flex-shrink-0"
          style={{ color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', textDecoration: 'none' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.75)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.4)'; }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
          Back
        </Link>
        {/* Branding */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff4655" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="1" fill="#ff4655" />
              <line x1="12" y1="3" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="21" />
              <line x1="3" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="21" y2="12" />
            </svg>
            <span className="text-sm font-bold tracking-tight" style={{ letterSpacing: '-0.01em' }}>
              fps<span style={{ color: '#ff4655' }}>.</span>expert
            </span>
          </div>
          <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.3)' }}>/ Timestamp Scrubber</span>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center px-4 py-8 gap-6 max-w-3xl mx-auto w-full">
        {/* Title */}
        <div className="text-center w-full">
          <h1 className="text-2xl font-bold mb-1" style={{ letterSpacing: '-0.02em' }}>Timestamp Scrubber</h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Upload your own video, find trigger moments, and build your personal reaction training config.
          </p>
        </div>

        {/* Load video */}
        <div className="w-full rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <p className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px' }}>Load Your Video</p>
          <div className="flex flex-col gap-3">
            {/* File upload */}
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all"
                style={{ background: 'rgba(0,229,255,0.12)', border: '1px solid rgba(0,229,255,0.3)', color: '#00e5ff', cursor: 'pointer', letterSpacing: '0.1em' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,229,255,0.2)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,229,255,0.12)'; }}>
                Choose File
              </button>
              {videoFileName && (
                <span className="text-xs font-mono" style={{ color: 'rgba(0,229,255,0.7)' }}>
                  {videoFileName}
                </span>
              )}
              {!videoFileName && (
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>or paste a URL below</span>
              )}
              <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleFileChange} />
            </div>
            {/* URL input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleUrlLoad(); }}
                placeholder="https://example.com/clip.mp4"
                className="flex-1 rounded-lg px-3 py-2 text-xs font-mono outline-none"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', caretColor: '#00e5ff' }}
              />
              <button onClick={handleUrlLoad}
                className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.12)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.07)'; }}>
                Load
              </button>
            </div>
          </div>
        </div>

        {/* Video player */}
        <div className="w-full rounded-2xl overflow-hidden" style={{ background: '#000', border: '1px solid rgba(255,255,255,0.08)', position: 'relative' }}>
          <div style={{ aspectRatio: '16/9', position: 'relative' }}>
            {videoSrc ? (
              <video
                ref={videoRef}
                src={videoSrc}
                className="w-full h-full object-contain"
                onLoadedMetadata={() => { if (videoRef.current) setDuration(videoRef.current.duration); }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.899L15 14" />
                  <rect x="3" y="6" width="12" height="12" rx="2" />
                </svg>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>No video loaded — upload a file or paste a URL above</p>
              </div>
            )}
          </div>
          {/* Seek bar with trigger markers — below video, inside the card */}
          {videoSrc && (
            <div className="px-4 pt-2 pb-1" style={{ background: 'rgba(0,0,0,0.6)' }}>
              <SeekBar
                currentTime={currentTime}
                duration={duration}
                triggers={triggers}
                fps={fps}
                onSeek={handleSeek}
              />
            </div>
          )}
        </div>

        {/* Controls row */}
        <div className="w-full flex flex-wrap items-center justify-between gap-4">
          {/* Playback controls */}
          <div className="flex items-center gap-2">
            <button onClick={() => stepFrame(-1)} disabled={!videoSrc}
              className="flex items-center justify-center w-10 h-10 rounded-xl transition-all"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: videoSrc ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)', cursor: videoSrc ? 'pointer' : 'not-allowed' }}
              title="Step back one frame">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
              </svg>
            </button>

            <button onClick={togglePlay} disabled={!videoSrc}
              className="flex items-center justify-center w-12 h-12 rounded-xl transition-all"
              style={{ background: videoSrc ? 'rgba(0,229,255,0.15)' : 'rgba(255,255,255,0.04)', border: `1px solid ${videoSrc ? 'rgba(0,229,255,0.4)' : 'rgba(255,255,255,0.08)'}`, color: videoSrc ? '#00e5ff' : 'rgba(255,255,255,0.2)', cursor: videoSrc ? 'pointer' : 'not-allowed' }}>
              {isPlaying ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
              )}
            </button>

            <button onClick={() => stepFrame(1)} disabled={!videoSrc}
              className="flex items-center justify-center w-10 h-10 rounded-xl transition-all"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: videoSrc ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)', cursor: videoSrc ? 'pointer' : 'not-allowed' }}
              title="Step forward one frame">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 18l8.5-6L6 6v12zm2-8.14L11.03 12 8 14.14V9.86zM16 6h2v12h-2z" />
              </svg>
            </button>
          </div>

          {/* FPS input */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px' }}>FPS</span>
            <input type="number" value={fpsInput} onChange={handleFpsChange} min={1} max={120} step={1}
              className="w-16 rounded-lg px-2 py-1.5 text-xs font-mono text-center outline-none"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', caretColor: '#00e5ff' }} />
          </div>
        </div>

        {/* Timestamp display + copy */}
        <div className="w-full rounded-2xl p-5" style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.2)' }}>
          <p className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: 'rgba(0,229,255,0.5)', fontSize: '10px' }}>Current Timestamp</p>
          <div className="flex flex-col gap-2 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>MM:SS.mmm</span>
              <span className="text-xl font-bold font-mono" style={{ color: '#00e5ff', letterSpacing: '0.05em' }}>
                {formatTimestamp(currentTime)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>Decimal (s)</span>
              <span className="text-xl font-bold font-mono" style={{ color: '#00e676', letterSpacing: '0.05em' }}>
                {currentTime.toFixed(6)}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={copyTimestamp} disabled={!videoSrc}
              className="flex-1 py-3 rounded-xl text-sm font-bold uppercase tracking-widest transition-all"
              style={{
                background: copied ? 'rgba(0,230,118,0.2)' : videoSrc ? 'rgba(0,229,255,0.15)' : 'rgba(255,255,255,0.04)',
                border: `1.5px solid ${copied ? 'rgba(0,230,118,0.5)' : videoSrc ? 'rgba(0,229,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                color: copied ? '#00e676' : videoSrc ? '#00e5ff' : 'rgba(255,255,255,0.2)',
                cursor: videoSrc ? 'pointer' : 'not-allowed',
                letterSpacing: '0.1em',
              }}>
              {copied ? '✓ Copied!' : 'Copy Timestamp'}
            </button>
            <button onClick={addTrigger} disabled={!videoSrc}
              className="flex-1 py-3 rounded-xl text-sm font-bold uppercase tracking-widest transition-all"
              style={{
                background: videoSrc ? 'rgba(255,70,85,0.15)' : 'rgba(255,255,255,0.04)',
                border: `1.5px solid ${videoSrc ? 'rgba(255,70,85,0.4)' : 'rgba(255,255,255,0.08)'}`,
                color: videoSrc ? '#ff4655' : 'rgba(255,255,255,0.2)',
                cursor: videoSrc ? 'pointer' : 'not-allowed',
                letterSpacing: '0.1em',
              }}
              onMouseEnter={(e) => { if (videoSrc) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.25)'; }}
              onMouseLeave={(e) => { if (videoSrc) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.15)'; }}>
              + Add Trigger
            </button>
          </div>
        </div>

        {/* Trigger list */}
        <div className="w-full rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between px-5 py-3" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: triggers.length > 0 ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
            <div className="flex items-center gap-2">
              <p className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px' }}>Trigger Timestamps</p>
              {triggers.length > 0 && (
                <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,70,85,0.15)', color: '#ff4655', fontSize: '9px' }}>
                  {triggers.length}
                </span>
              )}
            </div>
            {triggers.length > 0 && (
              <button onClick={exportConfig}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all"
                style={{ background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.25)', color: '#00e5ff', cursor: 'pointer', letterSpacing: '0.08em' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,229,255,0.18)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,229,255,0.1)'; }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export Config
              </button>
            )}
          </div>

          {triggers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 gap-2" style={{ background: 'rgba(255,255,255,0.01)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>No triggers added yet</p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.15)' }}>Scrub to a moment and click &quot;+ Add Trigger&quot;</p>
            </div>
          ) : (
            <div className="flex flex-col divide-y" style={{ divideColor: 'rgba(255,255,255,0.05)' }}>
              {triggers.map((trigger, idx) => (
                <div key={trigger.id} className="flex items-center gap-3 px-4 py-3" style={{ background: 'rgba(255,255,255,0.015)' }}>
                  {/* Index */}
                  <span className="text-xs font-mono w-5 text-center flex-shrink-0" style={{ color: 'rgba(255,255,255,0.25)' }}>
                    {idx + 1}
                  </span>

                  {/* Label (editable) */}
                  <div className="flex-1 min-w-0">
                    {editingLabelId === trigger.id ? (
                      <input
                        autoFocus
                        value={editingLabelValue}
                        onChange={(e) => setEditingLabelValue(e.target.value)}
                        onBlur={commitLabel}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitLabel(); if (e.key === 'Escape') setEditingLabelId(null); }}
                        className="w-full rounded px-2 py-0.5 text-xs font-mono outline-none"
                        style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.3)', color: '#00e5ff', caretColor: '#00e5ff' }}
                      />
                    ) : (
                      <button onClick={() => startEditLabel(trigger.id, trigger.label)}
                        className="text-left text-xs font-mono truncate w-full"
                        style={{ color: 'rgba(255,255,255,0.6)', cursor: 'text', background: 'none', border: 'none', padding: 0 }}
                        title="Click to rename">
                        {trigger.label}
                      </button>
                    )}
                  </div>

                  {/* Time */}
                  <button onClick={() => jumpToTrigger(trigger.time)}
                    className="text-xs font-mono flex-shrink-0 px-2 py-1 rounded transition-all"
                    style={{ color: '#ff4655', background: 'rgba(255,70,85,0.08)', border: '1px solid rgba(255,70,85,0.2)', cursor: 'pointer' }}
                    title="Jump to this timestamp"
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.18)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.08)'; }}>
                    {trigger.time.toFixed(3)}s
                  </button>

                  {/* Copy */}
                  <button onClick={() => copyTriggerTime(trigger.id, trigger.time)}
                    className="flex items-center justify-center w-7 h-7 rounded-lg transition-all flex-shrink-0"
                    style={{ background: copiedTriggerId === trigger.id ? 'rgba(0,230,118,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${copiedTriggerId === trigger.id ? 'rgba(0,230,118,0.4)' : 'rgba(255,255,255,0.08)'}`, color: copiedTriggerId === trigger.id ? '#00e676' : 'rgba(255,255,255,0.4)', cursor: 'pointer' }}
                    title="Copy decimal timestamp">
                    {copiedTriggerId === trigger.id ? (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    ) : (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
                    )}
                  </button>

                  {/* Delete */}
                  <button onClick={() => removeTrigger(trigger.id)}
                    className="flex items-center justify-center w-7 h-7 rounded-lg transition-all flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }}
                    title="Remove trigger"
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.12)'; (e.currentTarget as HTMLButtonElement).style.color = '#ff4655'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,70,85,0.3)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.3)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.07)'; }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* How to use */}
        <div className="w-full rounded-2xl p-5 mb-8" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs font-mono uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.25)', fontSize: '10px' }}>How to use</p>
          <ol className="flex flex-col gap-2">
            {[
              'Upload your own video clip using "Choose File" or paste a URL.',
              'Play the video and scrub to the exact moment a target appears.',
              'Click "+ Add Trigger" to mark that moment as a trigger timestamp.',
              'Add as many triggers as you need — they sort automatically by time.',
              'Click a timestamp badge to jump back to that moment for review.',
              'Use "Export Config" to download a ready-to-paste clip config snippet.',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{ background: 'rgba(0,229,255,0.1)', color: '#00e5ff', fontSize: '9px', marginTop: '1px' }}>
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>
      </main>
    </div>
  );
}
